import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { loginSuccess } from '../../redux/slices/authSlice';
import { updateUserInfo } from '../../redux/slices/userSlice';
import { clearAllSelections, resetTournamentState } from '../../redux/slices/tournamentSlice';
import { resetTournamentState as resetOrgTournamentState } from '../../redux/slices/organisationSlice';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
  LOCAL_STORAGE_KEYS,
} from '../../utils/localStorage';
import { DASHBOARD_STACK } from '../../utils/screenNames';
import {
  identifyUser,
  loginSucceeded,
  registerCompleted,
} from '../../utils/analytics';

/**
 * Custom hook for authentication operations
 * Centralizes auth logic to avoid duplication across login pages
 */
export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /**
   * Handle successful login/signup
   * Saves tokens, updates Redux, navigates to dashboard
   */
  const handleAuthSuccess = useCallback(
    (userData, redirectPath = DASHBOARD_STACK.HOME, options = {}) => {
      const { loginMethod, isSignup, isNewUser } = options;
      const { access, refresh } = userData.tokens;

      // Clear all Redux state first to prevent data leakage between accounts
      dispatch(clearAllSelections());
      dispatch(resetTournamentState());
      dispatch(resetOrgTournamentState());

      // Save to localStorage
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.USERNAME,
        userData?.user?.profile?.fullName || ''
      );
      setLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS, access);
      setLocalStorageItem(LOCAL_STORAGE_KEYS.REFRESH, refresh);

      // Update Redux
      dispatch(updateUserInfo({ userData: { ...userData.user } }));
      dispatch(loginSuccess({ access, isAuthenticated: true }));

      // Analytics — identify the user once, then fire the matching auth event
      // based on whether this was a fresh signup or an existing login.
      identifyUser(userData?.user);
      if (loginMethod) {
        if (isSignup) {
          registerCompleted({ signupMethod: loginMethod, user: userData?.user });
          // Meta Pixel: sign-up is the top-of-funnel conversion for paid ads.
          if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
            window.fbq('track', 'CompleteRegistration');
          }
        } else {
          loginSucceeded({ loginMethod, user: userData?.user, isNewUser });
        }
      }

      // Check for pending member invitation redirect
      const pending = getLocalStorageItem(LOCAL_STORAGE_KEYS.PENDING_MEMBER_INVITATION);
      if (pending?.token) {
        removeLocalStorageItem(LOCAL_STORAGE_KEYS.PENDING_MEMBER_INVITATION);
        redirectPath = `/member-invitation/${pending.token}`;
      }

      // Navigate
      navigate(redirectPath);
    },
    [dispatch, navigate]
  );

  /**
   * Handle auth failure
   * Can be customized with error callback
   */
  const handleAuthFailure = useCallback((error, onError) => {
    if (onError) {
      onError(error);
    }
  }, []);

  return {
    handleAuthSuccess,
    handleAuthFailure,
  };
};

export default useAuth;
